import React, { useContext } from 'react'
import { Tooltip as AntdTooltip } from 'antd'
import cnn from 'classnames'
import { ThemeContext } from 'styled-components'

import theme from 'components/styles/styledTheme'

const DEFAULT_STYLE = {
  padding: '10px',
  borderRadius: theme.borderRadius,
  color: theme.ds_gray_2,
  border: `1px solid ${theme.ds_blue_4}`,
}

/**
 * Tooltip component that wraps Ant Design's Tooltip with custom styling
 *
 * @param {Object} props - Component props
 * @param {string} [props.id] - Unique identifier for the tooltip
 * @param {string} [props.placement] - Tooltip placement position
 * @param {number} [props.mouseEnterDelay] - Delay before tooltip appears on mouse enter
 * @param {number} [props.mouseLeaveDelay] - Delay before tooltip disappears on mouse leave
 * @param {(string|React.ReactNode)} [props.text] - Content to display in the tooltip
 * @param {boolean} [props.arrow=false] - Whether to show an arrow pointing to the target
 * @param {string} [props.color] - Background color of the tooltip
 * @param {string} [props.styleType] - Style variant ('dark' or default)
 * @param {React.ReactNode} props.children - Content that triggers the tooltip
 * @param {boolean} [props.open] - Whether the tooltip is visible
 * @param {number} [props.zIndex] - CSS z-index of the tooltip
 * @param {Object} [props.align] - Alignment configuration
 * @param {boolean} [props.ignoreTooltip] - If true, renders children without tooltip wrapper
 * @param {Function} [props.onOpenChange] - Callback when tooltip visibility changes
 * @param {boolean} [props.destroyTooltipOnHide] - Whether to destroy tooltip when hidden
 * @param {string} [props.overlayClassName] - Additional class for tooltip overlay
 * @param {Object} [props.overlayStyle] - Additional style for tooltip overlay
 * @param {Object} [props.overlayInnerStyle] - Additional style for tooltip inner content
 * @param {string[]} [props.trigger] - Events that trigger the tooltip
 * @param {string} [props.childrenWrapperClassName] - Class for the children wrapper div
 * @param {Object} [props.childrenWrapperStyle] - Style for the children wrapper div
 * @param {Function} [props.getPopupContainer] - Function returning the container for the tooltip
 * @returns {React.ReactElement} Tooltip component
 */
const Tooltip = ({
  id,
  placement,
  mouseEnterDelay,
  mouseLeaveDelay,
  text,
  arrow = false,
  color,
  styleType,
  children,
  open,
  zIndex,
  align,
  ignoreTooltip,
  onOpenChange,
  destroyTooltipOnHide,
  overlayClassName,
  overlayStyle,
  overlayInnerStyle,
  trigger,
  childrenWrapperClassName,
  childrenWrapperStyle,
  getPopupContainer,
}) => {
  const theme = useContext(ThemeContext)

  const DARK_STYLE = {
    border: `1px solid ${theme.ds_gray_2}`,
    color: theme.white,
    fontWeight: theme.semibold,
    fontSize: theme.captionText,
  }

  const darkStyle = styleType === 'dark' ? DARK_STYLE : {}

  if (ignoreTooltip) return children

  return (
    <AntdTooltip
      id={id}
      trigger={trigger}
      overlayStyle={overlayStyle}
      overlayInnerStyle={{ ...DEFAULT_STYLE, ...overlayInnerStyle, ...darkStyle }}
      overlayClassName={cnn(overlayClassName, 'box-shadow-tooltip')}
      mouseEnterDelay={mouseEnterDelay}
      mouseLeaveDelay={mouseLeaveDelay}
      destroyTooltipOnHide={destroyTooltipOnHide}
      onOpenChange={onOpenChange}
      zIndex={zIndex}
      align={align}
      open={open}
      title={text}
      color={styleType === 'dark' ? theme.ds_gray_2 : color || theme.white}
      arrow={arrow}
      placement={placement}
      getPopupContainer={getPopupContainer || ((triggerNode) => triggerNode.parentNode)}
    >
      <div
        className={cnn(childrenWrapperClassName, 'tooltip-container')}
        style={{ display: 'inline-flex', ...childrenWrapperStyle }}
        id={id ? `${id}_Container` : undefined}
      >
        {children}
      </div>
    </AntdTooltip>
  )
}

export default Tooltip
